/* eslint-disable react/prop-types */
import {
  Grid,
  TextField,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import { DialogBox } from 'components';
import { strings } from 'i18n/Strings';
import Buttons from 'components/elements/Buttons';
import { type SearchUserDataType } from 'pages/Orders/TodayOrder';
import { handleNumberInputKeyDown } from 'utils/GeneralUtil';

interface Request {
  requestId: string;
  user: {
    firstName: string;
    lastName: string;
    email: string;
    uniqueId?: string;
  };
  pensionProvider?: string;
}

interface TransferInDialogBoxProps {
  openDialog: boolean;
  handleDialogClose: () => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  handleSearchUserForTranserIn: (value: string) => Promise<void>;
  searchedUserData: SearchUserDataType | null;
  pensionProvider: string;
  setPensionProvider: (value: string) => void;
  pensionProviderBankAccountName: string;
  setPensionProviderBankAccountName: (value: string) => void;
  createTransferInRequest: () => Promise<void>;
  editDialogOpen: boolean;
  handleEditDialogClose: () => void;
  editingRequest: Request | null;
  updateTransferInRequest: () => Promise<void>;
  loader: boolean;
  estimatedTransferValue: number | null;
  setEstimatedTransferValue: (value: number | null) => void;
  transferValueReceived: number | null;
  setTransferValueReceived: (value: number | null) => void;
  contributionEmployer: number | null;
  setContributionEmployer: (value: number | null) => void;
  contributionEmployee: number | null;
  setContributionEmployee: (value: number | null) => void;
  avc: number | null;
  setAvc: (value: number | null) => void;
  previousTransfer: number | null;
  setPreviousTransfer: (value: number | null) => void;
  sourceOfTransfer: string;
  setSourceOfTransfer: (value: string) => void;
}

const TransferInDialogBox = ({
  openDialog,
  handleDialogClose,
  searchValue,
  setSearchValue,
  handleSearchUserForTranserIn,
  searchedUserData,
  pensionProvider,
  setPensionProvider,
  pensionProviderBankAccountName,
  setPensionProviderBankAccountName,
  createTransferInRequest,
  editDialogOpen,
  handleEditDialogClose,
  editingRequest,
  updateTransferInRequest,
  loader,
  estimatedTransferValue,
  setEstimatedTransferValue,
  transferValueReceived,
  setTransferValueReceived,
  contributionEmployer,
  setContributionEmployer,
  contributionEmployee,
  setContributionEmployee,
  avc,
  setAvc,
  previousTransfer,
  setPreviousTransfer,
  sourceOfTransfer,
  setSourceOfTransfer,
}: TransferInDialogBoxProps): JSX.Element => {
  return (
    <>
      <DialogBox
        open={openDialog}
        onClose={handleDialogClose}
        title={strings.displayText.transferIn}
        fullWidth
        className="transfer-in-dialog"
      >
        <DialogContent style={{ display: 'flex', alignItems: 'center' }}>
          <TextField
            label={strings.displayText.enterEmailOrCustomer}
            variant="outlined"
            value={searchValue}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              setSearchValue(e.target.value);
            }}
            style={{ width: '400px' }}
          />
          <Buttons
            onClick={() => {
              void handleSearchUserForTranserIn(searchValue.trim());
            }}
            color="primary"
            style={{ marginLeft: '40px' }}
            disabled={searchValue === ''}
          >
            {strings.displayText.search}
          </Buttons>
        </DialogContent>
        <DialogContent>
          {searchedUserData && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px',
              }}
            >
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.firstName}
                  value={searchedUserData?.firstName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.lastName}
                  value={searchedUserData?.lastName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.email}
                  value={searchedUserData?.email}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.uniqueId}
                  value={searchedUserData?.uniqueId}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.pensionProvider}
                  margin="normal"
                  fullWidth
                  label={strings.displayText.pensionProvider}
                  value={pensionProvider}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPensionProvider(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.pensionProvider}
                  margin="normal"
                  fullWidth
                  label={strings.displayText.pensionProviderBankAccountName}
                  value={pensionProviderBankAccountName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPensionProviderBankAccountName(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.estimatedTransferValue}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.estimatedTransferValue}
                  value={estimatedTransferValue ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEstimatedTransferValue(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.transferValueReceived}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.transferValueReceived}
                  value={transferValueReceived ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setTransferValueReceived(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.contributionEmployer}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.contributionEmployer}
                  value={contributionEmployer ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setContributionEmployer(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.ContributionEmployee}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.ContributionEmployee}
                  value={contributionEmployee ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setContributionEmployee(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.avclabel}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.avclabel}
                  value={avc ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setAvc(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.previousTransfer}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.previousTransfer}
                  value={previousTransfer ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPreviousTransfer(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.sourceOfTransfer}
                  margin="normal"
                  fullWidth
                  label={strings.displayText.sourceOfTransfer}
                  value={sourceOfTransfer}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSourceOfTransfer(e.target.value);
                  }}
                />
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Buttons
            disabled={
              pensionProvider === '' ||
              pensionProviderBankAccountName === '' ||
              loader
            }
            onClick={() => {
              void createTransferInRequest();
            }}
            color="primary"
          >
            {strings.displayText.transferIn}
          </Buttons>
        </DialogActions>
      </DialogBox>

      <DialogBox
        open={editDialogOpen}
        onClose={handleEditDialogClose}
        title={strings.displayText.editTransferIn}
        fullWidth
        className="edit-transfer-in-dialog"
      >
        <DialogContent>
          {editingRequest && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '20px',
              }}
            >
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.firstName}
                  value={editingRequest.user.firstName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.lastName}
                  value={editingRequest.user.lastName}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.email}
                  value={editingRequest.user.email}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  margin="normal"
                  fullWidth
                  label={strings.displayText.uniqueId}
                  value={editingRequest.user.uniqueId}
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.pensionProvider}
                  margin="normal"
                  fullWidth
                  label={strings.displayText.pensionProvider}
                  value={pensionProvider}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPensionProvider(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.pensionProvider}
                  margin="normal"
                  fullWidth
                  label={strings.displayText.pensionProviderBankAccountName}
                  value={pensionProviderBankAccountName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPensionProviderBankAccountName(e.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.estimatedTransferValue}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.estimatedTransferValue}
                  value={estimatedTransferValue ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setEstimatedTransferValue(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.transferValuereceived}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.transferValuereceived}
                  value={transferValueReceived ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setTransferValueReceived(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.contributionEmployer}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.contributionEmployer}
                  value={contributionEmployer ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setContributionEmployer(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.ContributionEmployee}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.ContributionEmployee}
                  value={contributionEmployee ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setContributionEmployee(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.avclabel}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.avclabel}
                  value={avc ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setAvc(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.previousTransfer}
                  type="number"
                  margin="normal"
                  fullWidth
                  label={strings.displayText.previousTransfer}
                  value={previousTransfer ?? ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setPreviousTransfer(Number(e.target.value) || null);
                  }}
                  inputProps={{ min: 0 }}
                  onKeyDown={handleNumberInputKeyDown}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        {strings.displayText.eruo}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  name={strings.displayText.sourceOfTransfer}
                  margin="normal"
                  fullWidth
                  label={strings.displayText.sourceOfTransfer}
                  value={sourceOfTransfer}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    setSourceOfTransfer(e.target.value);
                  }}
                />
              </Grid>
            </div>
          )}
        </DialogContent>
        <DialogActions>
          <Buttons
            disabled={pensionProvider === '' || loader}
            onClick={() => {
              void updateTransferInRequest();
            }}
            color="primary"
            endIcon={loader ? <CircularProgress size={15} /> : null}
          >
            {strings.displayText.update}
          </Buttons>
        </DialogActions>
      </DialogBox>
    </>
  );
};

export default TransferInDialogBox;
